import React from 'react';
import Icon from '@/components/ui/AppIcon';

interface Phase {
  id: number;
  number: string;
  title: string;
  description: string;
  icon: string;
  deliverables: string[];
  duration: string;
}

const MethodologySection = () => {
  const phases: Phase[] = [
    {
      id: 1,
      number: '01',
      title: 'Discovery & Assessment',
      description: 'Comprehensive organisational readiness evaluation to identify capability gaps, cultural barriers, and transformation opportunities.',
      icon: 'MagnifyingGlassIcon',
      deliverables: [
        'AI Readiness Assessment Report',
        'Stakeholder Analysis & Mapping',
        'Change Impact Assessment',
        'Risk & Opportunity Matrix'
      ],
      duration: '2-4 weeks'
    },
    {
      id: 2,
      number: '02',
      title: 'Strategy & Planning',
      description: 'Development of tailored transformation roadmap aligned with business objectives and organisational capacity for change.',
      icon: 'MapIcon',
      deliverables: [
        'AI Transformation Roadmap',
        'Change Management Strategy',
        'Communication & Engagement Plan',
        'Success Metrics Framework'
      ],
      duration: '3-6 weeks'
    },
    {
      id: 3,
      number: '03',
      title: 'Capability Building',
      description: 'Targeted training and enablement programs to build confidence and competence across all levels of the organisation.',
      icon: 'AcademicCapIcon',
      deliverables: [
        'Role-Based Training Programs',
        'Leadership Coaching Sessions',
        'AI Literacy Workshops',
        'Change Champion Network'
      ],
      duration: '6-12 weeks'
    },
    {
      id: 4,
      number: '04',
      title: 'Implementation Guidance',
      description: 'Advisory support during AI deployment to ensure smooth adoption, address resistance, and maintain momentum.',
      icon: 'RocketLaunchIcon',
      deliverables: [
        'Pilot Program Advisory',
        'User Adoption Tracking',
        'Issue Resolution Support',
        'Process Integration Guidance'
      ],
      duration: '8-16 weeks'
    },
    {
      id: 5,
      number: '05',
      title: 'Optimization & Scale',
      description: 'Continuous improvement and expansion of AI capabilities based on performance data and user feedback.',
      icon: 'ArrowTrendingUpIcon',
      deliverables: [
        'Performance Analytics Dashboard',
        'Optimization Recommendations',
        'Scaling Strategy',
        'Governance Framework Updates'
      ],
      duration: 'Ongoing'
    }
  ];

  return (
    <section id="methodology" className="py-16 lg:py-24 bg-background">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full mb-4">
            <Icon name="CogIcon" size={20} variant="solid" className="text-primary" />
            <span className="text-sm font-semibold font-body text-primary">Our Approach</span>
          </div>
          
          <h2 className="text-3xl lg:text-4xl font-bold font-headline text-foreground mb-4">
            Assessment & Recognition Framework
          </h2>
          
          <p className="text-lg text-muted-foreground font-body leading-relaxed">
            Our proven five-phase framework ensures successful AI adoption by addressing both technical implementation and human change management in parallel.
          </p>
        </div>
        
        <div className="max-w-6xl mx-auto space-y-6">
          {phases.map((phase, index) => (
            <div
              key={phase.id}
              className="bg-card rounded-xl shadow-card hover:shadow-strong transition-all duration-300 border border-border overflow-hidden"
            >
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 p-6 lg:p-8">
                <div className="lg:col-span-1 flex lg:flex-col items-center lg:items-start gap-4">
                  <div className="text-5xl font-bold font-headline text-primary/20">
                    {phase.number}
                  </div>
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name={phase.icon as any} size={24} variant="outline" className="text-primary" />
                  </div>
                </div>
                
                <div className="lg:col-span-7">
                  <h3 className="text-xl font-bold font-headline text-foreground mb-3">
                    {phase.title}
                  </h3>
                  <p className="text-muted-foreground font-body mb-4 leading-relaxed">
                    {phase.description}
                  </p>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground font-body">
                    <Icon name="ClockIcon" size={16} variant="outline" className="text-primary" />
                    <span className="font-medium">Duration: {phase.duration}</span>
                  </div>
                </div>
                
                <div className="lg:col-span-4">
                  <h4 className="text-sm font-semibold font-headline text-foreground mb-3 flex items-center gap-2">
                    <Icon name="DocumentCheckIcon" size={16} variant="outline" className="text-success" />
                    Key Deliverables
                  </h4>
                  <ul className="space-y-2">
                    {phase.deliverables.map((deliverable, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <Icon name="CheckIcon" size={16} variant="solid" className="text-success mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-muted-foreground font-body">{deliverable}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              
              {index < phases.length - 1 && (
                <div className="flex justify-center pb-6">
                  <Icon name="ChevronDownIcon" size={24} variant="outline" className="text-primary/30" />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default MethodologySection;